import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useListMtoRunsQuery } from '@shared/api';
import type { MtoRun } from '@entities/mto';
import { isMtoModuleId, isMtoRunActive } from '@entities/mto';
import { useAgentPermission } from '@entities/user';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import { MtoVedomostStart } from '../MtoVedomostStart';
import { MtoReconcileStart } from '../MtoReconcileStart';
import { NewRunForm } from './components/NewRunForm';
import { RunsStack } from './components/RunsStack';
import { Column, Layout } from './styles';
import type { Scope } from './types';

/**
 * Роут `/agents/mto/runs`. Модуль приходит в `?module=` (выбирается в левом
 * рельсе оболочки). Если модуль валиден — слева форма запуска обработки этого
 * модуля, справа — его обработки. Без модуля — сводный реестр всех обработок
 * (по всем модулям), без формы. Polling пока есть активные обработки.
 */
export function MtoRunsList() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { can_write } = useAgentPermission('mto');
  const { activeProjectId, activeProjectName, setActiveProjectId } = useActiveProjectContext();

  const projectParam = params.get('project') || params.get('project_id');
  useEffect(() => {
    if (projectParam) {
      setActiveProjectId(projectParam);
    }
  }, [projectParam, setActiveProjectId]);

  // Активный модуль определяется URL (рельс навигации), а не локальным стейтом —
  // как у Экономиста. Невалидный/отсутствующий модуль = сводный реестр.
  const moduleParam = params.get('module');
  const activeModule = isMtoModuleId(moduleParam) ? moduleParam : null;

  const [scope, setScope] = useState<Scope>('all');
  const [poll, setPoll] = useState(0);

  // Фильтр по модулю — на сервере. Опрос — только пока есть активные
  // обработки (раньше список опрашивался каждые 4 с всегда).
  const listQ = useListMtoRunsQuery(
    { module: activeModule ?? undefined, scope, limit: 100 },
    { pollingInterval: poll },
  );
  const runs = useMemo<MtoRun[]>(() => {
    const items = listQ.data?.items ?? [];
    if (!activeProjectId && !activeProjectName) return items;
    return [...items].sort((a, b) => {
      const rawA = a.raw_data as { project_id?: string; project_name?: string } | null | undefined;
      const rawB = b.raw_data as { project_id?: string; project_name?: string } | null | undefined;
      const matchA =
        (activeProjectId && rawA?.project_id === activeProjectId) ||
        (activeProjectName && rawA?.project_name === activeProjectName)
          ? 0
          : 1;
      const matchB =
        (activeProjectId && rawB?.project_id === activeProjectId) ||
        (activeProjectName && rawB?.project_name === activeProjectName)
          ? 0
          : 1;
      return matchA - matchB;
    });
  }, [listQ.data, activeProjectId, activeProjectName]);

  const hasActive = useMemo(() => runs.some((r) => isMtoRunActive(r.status)), [runs]);
  useEffect(() => {
    setPoll(hasActive ? 4000 : 0);
  }, [hasActive]);

  const openRun = (rid: string) => {
    const matched = runs.find((r) => r.id === rid);
    const raw = matched?.raw_data as { project_id?: string; project_name?: string } | null | undefined;
    if (raw?.project_id) {
      setActiveProjectId(raw.project_id, { name: raw.project_name });
    }
    navigate(`/agents/mto/runs/${rid}`);
  };

  // Сводный реестр (модуль не выбран): все обработки, без формы запуска.
  if (!activeModule) {
    return (
      <RunsStack
        runs={runs}
        showModule
        busy={listQ.isFetching || hasActive}
        isLoading={listQ.isLoading}
        emptyText="Пока нет обработок. Выберите модуль слева и запустите обработку."
        scope={scope}
        onScope={setScope}
        onOpen={openRun}
      />
    );
  }

  // Ведомость материалов: выбор документов библиотеки РД во всю ширину, ниже —
  // ведомости отдела.
  if (activeModule === 'rd_extract') {
    return (
      <Column>
        <MtoVedomostStart />
        <RunsStack
          runs={runs}
          showModule={false}
          busy={listQ.isFetching || hasActive}
          isLoading={listQ.isLoading}
          emptyText="Пока нет ведомостей. Выберите объект и документы РД выше."
          scope={scope}
          onScope={setScope}
          onOpen={openRun}
        />
      </Column>
    );
  }

  if (activeModule === 'reconcile') {
    return (
      <Layout>
        <MtoReconcileStart />
        <RunsStack
          runs={runs}
          showModule={false}
          busy={listQ.isFetching || hasActive}
          isLoading={listQ.isLoading}
          emptyText="Пока нет сверок. Выберите ведомость и приложите заявки слева."
          scope={scope}
          onScope={setScope}
          onOpen={openRun}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <NewRunForm
        activeModule={activeModule}
        canWrite={can_write}
        onRunCreated={(runId) => navigate(`/agents/mto/runs/${runId}`)}
      />

      <RunsStack
        runs={runs}
        showModule={false}
        busy={listQ.isFetching || hasActive}
        isLoading={listQ.isLoading}
        emptyText="Пока нет обработок в этом модуле. Приложите документы слева и запустите обработку."
        scope={scope}
        onScope={setScope}
        onOpen={openRun}
      />
    </Layout>
  );
}

export default MtoRunsList;
